<template>
  <!--
    WP-03-CHAT-VISUAL-PARITY (doc 11) -- the prototype's three-step
    `.chat-progress` bar (زمینه سؤال / ارسال سؤال / پاسخ و ادامه). Purely
    presentational: step 1 is always done (the session exists), and `stage`
    says whether the student is still composing the first question ('ask')
    or the conversation has started ('answer'). No state of its own.
  -->
  <ol
    class="ai-tutor-progress"
    data-testid="ai-tutor-chat-progress"
    :aria-label="$t('aiTutorSetup.chat.progressLabel')"
  >
    <li
      v-for="(step, index) in steps"
      :key="step.key"
      class="ai-tutor-progress__step"
      :class="`ai-tutor-progress__step--${step.state}`"
      :aria-current="step.state === 'active' ? 'step' : null"
      :data-testid="`ai-tutor-progress-step-${step.key}`"
    >
      <span class="ai-tutor-progress__marker">
        <student-home-icon
          v-if="step.state === 'done'"
          name="check"
          :size="14"
        />
        <template v-else>{{ number(index + 1) }}</template>
      </span>
      <strong class="ai-tutor-progress__label">{{
        $t(`aiTutorSetup.chat.progress.${step.key}`)
      }}</strong>
    </li>
  </ol>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'

export default {
  name: 'ChatProgress',
  components: { StudentHomeIcon },
  props: {
    /** @type {import('vue').PropOptions<'ask'|'answer'>} */
    stage: {
      type: String,
      default: 'ask',
      validator: value => ['ask', 'answer'].includes(value),
    },
  },
  computed: {
    steps() {
      const asking = this.stage === 'ask'
      return [
        { key: 'context', state: 'done' },
        { key: 'ask', state: asking ? 'active' : 'done' },
        { key: 'answer', state: asking ? 'pending' : 'active' },
      ]
    },
  },
  methods: {
    number(value) {
      const locale =
        this.$i18n && this.$i18n.locale === 'fa' ? 'fa-IR' : 'en-US'
      return value.toLocaleString(locale)
    },
  },
}
</script>

<style lang="scss" scoped>
// Every value below was measured from the rendered canonical prototype
// (`.chat-progress`, sha256 75678bee...; 1920x1040 and 390x844), not
// approximated -- see doc 11 (WP-03-CHAT-VISUAL-PARITY).
$ai-tutor-muted: $borna-muted;
$ai-tutor-border: $borna-border;
$ai-tutor-canvas: $borna-canvas;
$ai-tutor-indigo-50: $borna-indigo-50;
$ai-tutor-indigo-300: $borna-indigo-300;
$ai-tutor-indigo-600: $borna-indigo-600;
$ai-tutor-indigo-700: $borna-indigo-900;
$ai-tutor-teal-50: $borna-teal-50;
$ai-tutor-teal-100: $borna-teal-100;
$ai-tutor-teal-700: $borna-teal-700;

.ai-tutor-progress {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
  margin: 0 0 12px;
  padding: 0;
  list-style: none;
}

.ai-tutor-progress__step {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid $ai-tutor-border;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.68);
  color: $ai-tutor-muted;

  &--done {
    border-color: $ai-tutor-teal-100;
    color: $ai-tutor-teal-700;
  }

  &--active {
    border-color: $ai-tutor-indigo-300;
    background: $ai-tutor-indigo-50;
    color: $ai-tutor-indigo-700;
  }
}

.ai-tutor-progress__marker {
  display: grid;
  place-items: center;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: $ai-tutor-canvas;
  font-size: 9px;
  font-weight: 900;

  .ai-tutor-progress__step--done & {
    background: $ai-tutor-teal-50;
    color: $ai-tutor-teal-700;
  }

  .ai-tutor-progress__step--active & {
    background: $ai-tutor-indigo-600;
    color: #fff;
  }
}

.ai-tutor-progress__label {
  overflow: hidden;
  font-size: 9px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// The prototype's <=620px rule.
@media (max-width: 620px) {
  .ai-tutor-progress {
    gap: 5px;
  }

  .ai-tutor-progress__step {
    justify-content: center;
    padding: 7px 5px;
  }

  .ai-tutor-progress__marker {
    flex-basis: 21px;
    width: 21px;
    height: 21px;
  }

  .ai-tutor-progress__label {
    font-size: 8px;
  }
}
</style>
